/**
 * The cost model (SPEC.md §4.1's fourth hardest mechanic; CR §4.3.2, §1.5.5, §6.1.7).
 *
 * ```
 * total cost = (ink cost | one chosen alternate cost) ± every payment modifier that applies
 * ```
 *
 * Two halves live in two modules, and the split is deliberate:
 *
 * - **this one** answers *what does it cost* — the payment-modifier records, the total ink
 *   cost, and the value of each alternate cost (the singing requirement, the Shift cost and
 *   which cards a Shift may be played onto). It reads state and printed data only, so it has
 *   no zone-moving dependencies and `characteristics.ts` can re-export `costOf` from it;
 * - **`payments.ts`** answers *how may it be paid* — it enumerates every legal payment as a
 *   keyed option and performs one.
 *
 * ## Payment modifiers (CR §4.3.2.2, §6.6.4)
 *
 * A payment modifier changes what you pay, never the card's printed **cost characteristic**:
 * "you pay 2 {I} less to play this" does not make a 5-cost character a 3-cost character for
 * "chosen character with cost 3 or less". That is why the adjustment here is applied only
 * while the card sits in the zone it is being played *from* (a hand today), and why the
 * selector limiters in `abilities/selectors.ts` — which look at cards in play — read the same
 * accessor and get the printed number back.
 *
 * They come from three places, all summed by `paymentAdjustment`:
 *
 * 1. **Underdog** (CR §8.13) — the one printed keyword that is a payment modifier, and it
 *    functions from **hand** (CR §6.1.12: abilities that work out of play);
 * 2. **floating records** in `GameState.costModifiers` — what a resolved `modify-cost` effect
 *    leaves behind ("the next character you play this turn costs 2 {I} less"). They stack,
 *    they carry a duration, and a one-shot record is consumed by the payment it applies to;
 * 3. **continuous statics** on a card in play — or, per CR §6.1.12, in a **hand or discard** —
 *    whose `cost-modifier` effect matches the card being played.
 *
 * The total is clamped at zero (CR §6.6.4: a cost can never be reduced below 0 {I}).
 *
 * ## What this module deliberately does not do
 *
 * Stat modifiers, granted keywords and their durations are ticket 10's `Modifier` record and
 * its `bonusFor`/`grantedByStatics` read paths; a cost modifier is a *payment* modifier and
 * keeps its own record kind, its own list and its own expiry (`expireCostModifiers`) so the
 * two models never have to agree on a duration vocabulary they do not share.
 */
import type { Amount, CostApplies, Duration, PlayerRef, Selector } from './abilities/ir.ts';
import {
  authoredAbilities,
  hasKeyword,
  keywordValue,
  normaliseKeyword,
  singerValueOf,
  staticAbilitiesOf,
} from './abilities/keywords.ts';
import { baseContext, evalAmount, evalCondition, resolveCardRefAllIn } from './abilities/selectors.ts';
import type { StaticAbility } from './abilities/ir.ts';
import type { CardCatalog, CardDefinition } from './cards.ts';
import { cardNamesOf, requireDefinition } from './cards.ts';
import type { InstanceId, PlayerIndex, Zone } from './ids.ts';
import type { DurationBoundary } from './modifiers.ts';
import type { CardInstance, GameState } from './state.ts';
import { instanceAt, isStartingPlayer, isYourFirstTurn, nextPlayer, playerAt } from './state.ts';

// ---------------------------------------------------------------------------
// How a card is being paid for
// ---------------------------------------------------------------------------

/**
 * The payment a `play` option stands for — the "announce your payment method" step of
 * playing a card (CR §4.3.1.2). An alternate cost is a **complete substitute** for the ink
 * cost, never a discount on it, so this is a choice between whole methods rather than a set
 * of flags.
 *
 * It rides inside the `play` action, which is what makes every legal way to pay a distinct
 * keyed option (`play:d12`, `play:d12:sing:i3+i7`) rather than a follow-up question. The ids
 * it names are always **public** cards (in play, or in a discard); the card being played is
 * keyed by `defId` because a hand is hidden (SPEC.md §4.4).
 */
export type PaymentPlan =
  /** The printed ink cost, after payment modifiers. The default, and the only one until 09. */
  | { readonly kind: 'ink' }
  /** "For free" (CR §6.1.7) — pay nothing, and no alternate cost may be opted into. */
  | { readonly kind: 'free' }
  /** Singing (CR §5.4.4): exert these characters instead of paying the song's ink cost. */
  | { readonly kind: 'sing'; readonly singers: readonly InstanceId[] }
  /** Shift (CR §8.10): pay the Shift cost and play this on top of these cards. */
  | { readonly kind: 'shift'; readonly onto: readonly InstanceId[] }
  /**
   * An alternate cost paid by **banishing cards you control** (CR §1.5.5) — "banish chosen
   * item of yours to play this character for free". Like every alternate cost it replaces the
   * ink cost outright, so no ink is paid at all.
   */
  | { readonly kind: 'banish'; readonly banish: readonly InstanceId[] }
  /**
   * An alternate cost paid by **exerting cards you control** (CR §1.5.5) — "exert 4 items of
   * yours to play this character for free" (Scrooge McDuck - Resourceful Miser's PUT IT TO
   * GOOD USE). The `banish` variant's sibling: no ink is paid, and the named cards pay `{E}`
   * rather than leaving play.
   */
  | { readonly kind: 'exert'; readonly exert: readonly InstanceId[] }
  /** Move these public discard cards to the bottom of their owner's deck as the payment. */
  | {
      readonly kind: 'move-to-bottom-of-deck';
      readonly cards: readonly InstanceId[];
    }
  /**
   * Shift, paid by an alternate cost instead of ink (CR §1.5.5 layered onto CR §8.10) —
   * Maleficent & Diablo - Evil Incarnate's FOOLS!: "put 5 character cards from your discard on
   * the bottom of your deck in any order to shift this character for free." `onto` is the
   * Shift base set, exactly like the `shift` plan; `method`/`cards` is the alternate-cost
   * payment named on the card (ticket 98's shapes), which pays for it instead of ink.
   */
  | {
      readonly kind: 'shift-alternate-cost';
      readonly onto: readonly InstanceId[];
      readonly method: 'banish' | 'exert' | 'move-to-bottom-of-deck';
      readonly cards: readonly InstanceId[];
    };

/** The ink payment, the plan every card has. */
export const INK_PAYMENT: PaymentPlan = { kind: 'ink' };

// ---------------------------------------------------------------------------
// Payment modifier records
// ---------------------------------------------------------------------------

/** Which payment a modifier applies to — `play` by default, or one named payment/action. */
export type { CostApplies } from './abilities/ir.ts';

/**
 * One floating payment modifier (CR §4.3.2.2).
 *
 * `player` is whose payments it applies to and `filter` which cards, read against **printed**
 * characteristics — a cost filter that read a modified cost would recurse through `costOf`.
 * `oneShot` is the "the next [card] you play this turn" shape: the record is consumed by the
 * payment it applies to, including a payment that turned out to cost nothing, because CR 2.2
 * has a card played for free still allow its payment modifiers to have applied.
 */
/**
 * What every payment-modifier record carries. `player` is whose payments it changes;
 * `controller` is whose effect it *is* — the "your" of "until the start of your next turn",
 * which is a different seat whenever a card changes what an opponent pays.
 */
interface CostModifierProvenance {
  readonly source: InstanceId | null;
  readonly player: PlayerIndex;
  readonly filter?: Selector;
  readonly duration: Duration;
  readonly controller?: PlayerIndex;
  /** `state.turn` when the record was made — what a turn-boundary duration measures from. */
  readonly created?: number;
}

export type CostModifier =
  | (CostModifierProvenance & {
      readonly kind: 'amount';
      /** Negative reduces, positive increases. */
      readonly amount: number;
      readonly applies?: CostApplies;
      readonly oneShot?: boolean;
      /**
       * The **specific instances** this record was bound to as it resolved — "chosen character
       * counts as having +3 cost to sing songs this turn". A bound record ignores `filter` and
       * `player` alike: the cards it names are its whole scope, so a second copy of the same
       * card in play, and a card that arrives afterwards, are untouched. Absent for the
       * ordinary filter-matched record, which re-matches on every payment it is asked about.
       */
      readonly cards?: readonly InstanceId[];
    })
  /** "You may play [X] for free this turn" — a permission, not an amount (CR §6.1.7). */
  | (CostModifierProvenance & {
      readonly kind: 'free';
      readonly oneShot?: boolean;
    })
  /**
   * "Opponents can't play actions until the start of your next turn" — a permission
   * **removed** (CR §6.1.10). `legalPlayOptions` drops every option for a card it matches, so
   * the restriction is invisible to the AI and the UI alike: the move simply is not there.
   */
  | (CostModifierProvenance & { readonly kind: 'forbid' });

/** Record a payment modifier. The list is flat and unordered — modifiers simply sum. */
export function addCostModifier(state: GameState, modifier: CostModifier): void {
  state.costModifiers.push(modifier);
}

/**
 * Drop the payment modifiers whose duration has run out at this boundary — the same two
 * boundaries and the same reading `modifiers.ts` uses for continuous effects, so a "this turn"
 * reduction and a "this turn" buff end at the same moment.
 */
export function expireCostModifiers(
  state: GameState,
  boundary: DurationBoundary = 'end-of-turn',
): void {
  state.costModifiers = state.costModifiers.filter(
    (each) => !costModifierExpired(state, each, boundary),
  );
}

function costModifierExpired(
  state: GameState,
  modifier: CostModifier,
  boundary: DurationBoundary,
): boolean {
  switch (modifier.duration) {
    case 'this-turn':
      return boundary === 'end-of-turn';
    case 'until-start-of-your-next-turn':
      return (
        boundary === 'start-of-turn' &&
        state.activePlayer === (modifier.controller ?? modifier.player) &&
        state.turn > (modifier.created ?? state.turn)
      );
    case 'until-end-of-their-next-turn':
      return boundary === 'end-of-turn' && state.turn > (modifier.created ?? state.turn);
    case 'permanent':
      return false;
  }
}

/**
 * Whether a card being paid for matches a modifier's filter.
 *
 * **Printed characteristics only**, and the card's zone is not consulted: the card is the
 * subject of the payment, wherever it is being played from. `controller` defaults to `you`
 * ("you pay 1 {I} less" is about your own plays), which is the one place this differs from
 * `matchesSelector`'s `any` default.
 */
export function matchesCostFilter(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
  filter?: Selector,
  /** The card the modifier is printed on — what `Selector.self` means here. */
  source?: InstanceId,
  /**
   * Where `Selector.here` means (CR §5.6) — the modifier source's own location, computed by
   * `costHereOf`. `undefined` for a modifier this module has no location context for (a
   * floating `costModifiers` record), which is why an absent value simply fails a `here`
   * filter instead of matching everything.
   */
  here?: InstanceId | null,
): boolean {
  if (filter === undefined) return true;
  // A disjunction, read the same way `matchesSelector` reads it: each branch on top of the
  // limiters the whole filter shares.
  if (filter.or !== undefined) {
    const { or, ...shared } = filter;
    if (
      !or.some((each) => matchesCostFilter(state, player, card, { ...shared, ...each }, source, here))
    ) {
      return false;
    }
  }
  // "This card only" — the `thisCard()` filter a card uses to talk about its own payments.
  // Without this a cost modifier written that way would widen to every card its controller
  // plays, which is a rules bug the type system cannot see.
  if (filter.self === true && card.id !== source) return false;
  // "Characters … while here" (CR §5.6) — a location's own continuous cost modifier, scoped to
  // whoever is standing at it right now, whatever their controller.
  if (filter.here === true && (here == null || card.location === null || card.location !== here)) {
    return false;
  }
  const controller = filter.controller ?? 'you';
  if (controller === 'you' && card.controller !== player) return false;
  if (controller === 'opponent' && card.controller === player) return false;
  const def = state.catalog.get(card.defId);
  if (def === undefined) return false;
  if (filter.types !== undefined && !filter.types.includes(def.type)) return false;
  if (filter.subtype !== undefined && !def.subtypes.includes(filter.subtype)) return false;
  if (
    filter.anySubtype !== undefined &&
    !filter.anySubtype.some((each) => def.subtypes.includes(each))
  ) {
    return false;
  }
  if (filter.withoutSubtype !== undefined && def.subtypes.includes(filter.withoutSubtype)) {
    return false;
  }
  if (filter.name !== undefined && !def.names.includes(filter.name)) return false;
  if (filter.withoutName !== undefined) {
    if (typeof filter.withoutName === 'string') {
      if (def.fullName === filter.withoutName || cardNamesOf(def).includes(filter.withoutName)) {
        return false;
      }
    } else if (source !== undefined) {
      const ctx = baseContext(source, player, null);
      const candidateNames = cardNamesOf(def);
      const sharesName = resolveCardRefAllIn(state, ctx, filter.withoutName).some((id) => {
        const referenced = state.instances[id];
        if (referenced === undefined) return false;
        return cardNamesOf(requireDefinition(state.catalog, referenced.defId)).some((name) =>
          candidateNames.includes(name),
        );
      });
      if (sharesName) return false;
    }
  }
  if (filter.keyword !== undefined && !hasKeyword(state, card, filter.keyword)) return false;
  if (filter.withoutKeyword !== undefined && hasKeyword(state, card, filter.withoutKeyword)) {
    return false;
  }
  // Instance states, not characteristics: reading them cannot recurse through `costOf`.
  if (filter.exerted !== undefined && card.exerted !== filter.exerted) return false;
  if (filter.damaged !== undefined && card.damage > 0 !== filter.damaged) return false;
  if (filter.costAtMost !== undefined) {
    // A cost-modifier filter has no `AbilityContext` of its own (this module deliberately
    // reads printed data only, per the header) — build a minimal one only when a computed cap
    // needs it, exactly as `withoutName`'s reference form does a few lines up.
    const cap =
      typeof filter.costAtMost === 'number'
        ? filter.costAtMost
        : evalAmount(state, baseContext(source ?? card.id, player, null), filter.costAtMost);
    if (def.cost > cap) return false;
  }
  if (filter.costAtLeast !== undefined && def.cost < filter.costAtLeast) return false;
  if (filter.strengthAtMost !== undefined && (def.strength ?? 0) > filter.strengthAtMost) {
    return false;
  }
  if (filter.strengthAtLeast !== undefined && (def.strength ?? 0) < filter.strengthAtLeast) {
    return false;
  }
  return true;
}

/** A `cost-modifier` static ability and the card it is printed on. */
interface StaticCostModifier {
  readonly source: CardInstance;
  readonly amount: number;
  readonly applies: CostApplies;
  readonly filter: Selector;
  /** For `applies: 'move'`: matched against the moving character, not the destination. */
  readonly mover?: Selector;
  /** For `applies: 'shift'`: matched against the chosen base, not the card being played. */
  readonly onto?: Selector;
  /** "Once during your turn, …" (CR §6.1.13.2) — present only for a limited modifier. */
  readonly limit?: 'once-per-turn';
  /** The `GameState.abilityUses` key this modifier's once-per-turn slot is tracked under. */
  readonly useKey?: string;
}

// ---------------------------------------------------------------------------
// The no-cycle boundary: reading a cost static's own amount and "while" guard
// ---------------------------------------------------------------------------

/**
 * A cost static may carry a live `Amount` — "for each exerted character opponents have in
 * play, you pay 1 {I} less". It is counted **here**, against printed characteristics and
 * instance states only, rather than through `abilities/selectors.ts`.
 *
 * That is the module's layering rule (ticket 09): `evalAmount` reads `costOf`, so counting a
 * cost static's own amount through it would make the cost model depend on itself. The subset
 * below is exactly what printed cost text uses — counting a pool, summing its damage,
 * negating — and anything outside it contributes nothing rather than opening the cycle.
 */
function costPool(state: GameState, filter: Selector, controller: PlayerIndex): CardInstance[] {
  const zone: Zone = filter.zone ?? 'play';
  const found: CardInstance[] = [];
  for (const player of state.players) {
    for (const id of zoneOfPlayer(player, zone)) {
      const card = instanceAt(state, id);
      if (matchesCostFilter(state, controller, card, filter)) found.push(card);
    }
  }
  return found;
}

function zoneOfPlayer(player: GameState['players'][number], zone: Zone): readonly InstanceId[] {
  switch (zone) {
    case 'deck':
      return player.deck;
    case 'hand':
      return player.hand;
    case 'play':
      return player.play;
    case 'inkwell':
      return player.inkwell;
    case 'discard':
      return player.discard;
    case 'under':
      return player.under;
  }
}

function costAmount(
  state: GameState,
  amount: Amount,
  controller: PlayerIndex,
  /** The card the static is printed on — what `own-move-cost` reads. */
  source?: CardInstance,
): number {
  if (typeof amount === 'number') return amount;
  switch (amount.kind) {
    case 'minus':
      return -costAmount(state, amount.of, controller, source);
    case 'times':
      return amount.n * costAmount(state, amount.of, controller, source);
    // "Your Pirate characters may move here for free": the reduction is the destination's own
    // printed move cost, read here rather than restated by the card module.
    case 'own-move-cost':
      return source === undefined ? 0 : (state.catalog.get(source.defId)?.moveCost ?? 0);
    case 'count':
      return costPool(state, amount.of, controller).length;
    case 'damage-total':
      return costPool(state, amount.of, controller).reduce((sum, card) => sum + card.damage, 0);
    // "For each opposing character banished in a challenge this turn" — the per-turn fact,
    // matched on printed characteristics only: the banished card is in a discard by now, and
    // `matchesCostFilter` never consults zones anyway.
    case 'banished-in-challenge-this-turn-count':
      return state.banishedInChallengeThisTurn.filter((id) => {
        const card = state.instances[id];
        return card !== undefined && matchesCostFilter(state, controller, card, amount.of);
      }).length;
    // The rest of the per-turn scalars, read the same way: plain counts kept on the state, so
    // they answer here without going through `characteristics.ts` and without a cycle.
    case 'challenges-this-turn':
      return state.challengedThisTurn.length;
    case 'cards-inked-this-turn':
      return playerAt(state, costPlayer(state, amount.player, controller)).cardsInkedThisTurn;
    case 'cards-drawn-this-turn':
      return playerAt(state, costPlayer(state, amount.player, controller)).cardsDrawnThisTurn;
    case 'damage-removed-this-turn':
      return amount.player === undefined
        ? state.damageRemovedThisTurn.reduce((total, removed) => total + removed, 0)
        : (state.damageRemovedThisTurn[costPlayer(state, amount.player, controller)] ?? 0);
    case 'cards-put-into-discard-this-turn':
      return playerAt(state, costPlayer(state, amount.player, controller)).cardsPutIntoDiscardThisTurn;
    case 'cards-discarded-this-turn':
      return playerAt(state, costPlayer(state, amount.player, controller)).cardsDiscardedThisTurn;
    case 'played-this-turn-count':
      return state.playedThisTurn.filter((id) => {
        const card = state.instances[id];
        return card !== undefined && matchesCostFilter(state, controller, card, amount.of);
      }).length;
    default:
      // A characteristic read would have to go through `characteristics.ts`; no printed cost
      // text needs one, so it contributes nothing rather than opening the cycle.
      return 0;
  }
}

/**
 * `abilities/selectors.ts`'s `resolvePlayerRef`, for a module with no `AbilityContext`: the
 * "you" of a cost static is the player being charged. A reference that names a card's
 * controller has no card to read here and falls back to that same player, which is the one
 * this module can honestly answer for.
 */
function costPlayer(state: GameState, ref: PlayerRef, controller: PlayerIndex): PlayerIndex {
  switch (ref.kind) {
    case 'player':
      return ref.player;
    case 'opponent':
      return nextPlayer(state, controller);
    case 'controller':
    case 'card-controller':
      return controller;
    // A `choose-player` binding (ticket 102) only ever exists inside an effect tree, never a
    // cost — a cost is paid before an ability's effect resolves, so no `PlayerTargetSpec` this
    // module could read has bound anything yet. Same fallback as `card-controller`'s "no card
    // to read here": the one player this module can honestly answer for.
    case 'chosen':
      return controller;
  }
}

/**
 * The cost model's own copy of `abilities/selectors.ts`'s `hereOf` (CR §5.6): a location's own
 * id, or a character's, kept separate because this module reads printed characteristics and
 * instance states only (this module's layering rule) rather than through `AbilityContext`.
 */
function costHereOf(state: GameState, source: CardInstance): InstanceId | null {
  const def = state.catalog.get(source.defId);
  return def?.type === 'Location' ? source.id : source.location;
}

/**
 * Whether a static's own "while …" condition holds (CR §6.4.4). Evaluated against the card
 * the ability is printed on, exactly as `modifiers.ts` does for `buff` and `grant-keyword` —
 * a cost modifier is no less conditional than they are.
 */
function staticIsActive(state: GameState, source: CardInstance, ability: StaticAbility): boolean {
  if (ability.while === undefined) return true;
  return evalCondition(
    state,
    baseContext(source.id, source.controller, source.id),
    ability.while,
  );
}

/**
 * Every continuous static cost modifier currently functioning (CR §6.4.2.1, §6.1.12).
 *
 * `from` is where the printed card has to be for its ability to work — `play` for an ordinary
 * static, `hand` for an Underdog-shaped one, `discard` for "while this is in your discard,
 * you pay 1 {I} less …". Computed on read, never stored, so a card that arrives (or is
 * discarded) later is accounted for immediately.
 */
function staticCostModifiers(state: GameState): StaticCostModifier[] {
  let found: StaticCostModifier[] | null = null;
  for (const source of state.instances) {
    const statics = costStaticsOf(state.catalog, source.defId);
    if (statics.length === 0) continue;
    statics.forEach((ability, index) => {
      if (ability.effect.kind !== 'cost-modifier') return;
      const from: Zone = ability.effect.from ?? 'play';
      if (source.zone !== from) return;
      // "While …" / "If you have a character named Gaston in play, …" — the same live guard the
      // characteristic statics honour (CR §6.4.4), evaluated with this card as `self`.
      if (!staticIsActive(state, source, ability)) return;
      const limit = ability.effect.limit;
      // "Once during your turn, …" (CR §6.1.13.2) — a slot already spent this turn drops the
      // record entirely, so `paymentAdjustment` prices the payment at full cost the same as if
      // the static were not there. `index` is this ability's stable position among the card's
      // own cost statics (memoized per catalog+defId), mirroring `activationUseKey`'s ability
      // index for an activated ability's own once-per-turn limit.
      const useKey = limit === undefined ? undefined : costModifierUseKey(source.id, index);
      if (useKey !== undefined && state.abilityUses.includes(useKey)) return;
      (found ??= []).push({
        source,
        amount: costAmount(state, ability.effect.amount, source.controller, source),
        applies: ability.effect.applies ?? 'play',
        filter: ability.effect.filter,
        ...(ability.effect.mover === undefined ? {} : { mover: ability.effect.mover }),
        ...(ability.effect.onto === undefined ? {} : { onto: ability.effect.onto }),
        ...(limit === undefined ? {} : { limit }),
        ...(useKey === undefined ? {} : { useKey }),
      });
    });
  }
  return found ?? NO_STATIC_COST_MODIFIERS;
}

/** Shared empty result — most reads find no static cost modifier anywhere. */
const NO_STATIC_COST_MODIFIERS: StaticCostModifier[] = [];

/**
 * The payment statics among a card's authored IR — `cost-modifier` and `free-play`, the two
 * `StaticEffect` kinds this module reads — memoized per catalog. Printed data only, like
 * `staticAbilitiesOf`, so it cannot go stale. Whether each one *functions* (its zone, its
 * "while") stays a live read in `staticCostModifiers` / `staticFreePlayFor`.
 */
const COST_STATICS_MEMO = new WeakMap<CardCatalog, Map<number, readonly StaticAbility[]>>();

// One catalog per game: pay the WeakMap hop per catalog switch, not per instance scanned.
let costStaticsCatalog: CardCatalog | null = null;
let costStaticsByDef: Map<number, readonly StaticAbility[]> | null = null;

function costStaticsOf(catalog: CardCatalog, defId: number): readonly StaticAbility[] {
  if (catalog !== costStaticsCatalog) {
    let byDef = COST_STATICS_MEMO.get(catalog);
    if (byDef === undefined) {
      byDef = new Map();
      COST_STATICS_MEMO.set(catalog, byDef);
    }
    costStaticsCatalog = catalog;
    costStaticsByDef = byDef;
  }
  const byDef = costStaticsByDef as Map<number, readonly StaticAbility[]>;
  let statics = byDef.get(defId);
  if (statics === undefined) {
    statics = staticAbilitiesOf(catalog, defId).filter(
      (ability) => ability.effect.kind === 'cost-modifier' || ability.effect.kind === 'free-play',
    );
    if (statics.length === 0) statics = NO_STATIC_COST_STATICS;
    byDef.set(defId, statics);
  }
  return statics;
}

const NO_STATIC_COST_STATICS: readonly StaticAbility[] = [];

/** The `GameState.abilityUses` key for a once-per-turn static cost modifier (CR §6.1.13.2). */
export function costModifierUseKey(instance: InstanceId, index: number): string {
  return `cost:i${instance}:s${index}`;
}

/**
 * Every static cost modifier that matches one payment — `staticCostModifiers`' whole list,
 * narrowed by `applies` and by the filter/`mover` language `paymentAdjustment` sums. Split out
 * so `consumeOnceThisTurnCostModifiers` can find exactly the records a payment actually drew on,
 * without re-deriving the matching rules a second time.
 */
function matchingStaticCostModifiers(
  state: GameState,
  card: CardInstance,
  applies: CostApplies,
  mover?: CardInstance,
  /** For `applies: 'shift'`: the chosen base(s) — `mover`'s onto-keyed twin. */
  onto?: readonly CardInstance[],
): StaticCostModifier[] {
  const found: StaticCostModifier[] = [];
  for (const record of staticCostModifiers(state)) {
    if (record.applies !== applies) continue;
    // The static's own controller is the "you" of its filter; a card played by anyone else
    // only matches a filter that says so explicitly.
    if (
      !matchesCostFilter(
        state,
        record.source.controller,
        card,
        record.filter,
        record.source.id,
        costHereOf(state, record.source),
      )
    ) {
      continue;
    }
    // Mover-keyed: the same filter language, read against the moving character instead.
    if (
      record.mover !== undefined &&
      (mover === undefined ||
        !matchesCostFilter(
          state,
          record.source.controller,
          mover,
          record.mover,
          record.source.id,
          costHereOf(state, record.source),
        ))
    ) {
      continue;
    }
    // Onto-keyed: the same filter language, read against the chosen Shift base(s) instead —
    // either base of a Duo/Combo pair landing on the named card is enough (CR §8.10.4: both
    // bases go under the one card that shifted onto them).
    if (
      record.onto !== undefined &&
      (onto === undefined ||
        !onto.some((base) =>
          matchesCostFilter(
            state,
            record.source.controller,
            base,
            record.onto,
            record.source.id,
            costHereOf(state, record.source),
          ),
        ))
    ) {
      continue;
    }
    found.push(record);
  }
  return found;
}

/**
 * The sum of every payment modifier that applies to one payment — the `±` of the total-cost
 * formula. Negative reduces. Not clamped: the clamp belongs to the total, not to the sum
 * (CR §6.6.4), so an increase can cancel a reduction.
 */
export function paymentAdjustment(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
  applies: CostApplies = 'play',
  /**
   * For `applies: 'move'`: the character being moved, so a modifier with a `mover` filter can
   * key on who is moving ("your Pirate characters may move here for free") rather than on the
   * destination the payment is about. A mover-keyed modifier never matches without one.
   */
  mover?: CardInstance,
  /**
   * For `applies: 'shift'`: the chosen base(s), so a modifier with an `onto` filter can key on
   * which base the Shift lands on ("you pay 1 {I} less to shift a character on top of this
   * character") rather than on the card being played. `mover`'s onto-keyed twin; an onto-keyed
   * modifier never matches without one.
   */
  onto?: readonly CardInstance[],
): number {
  let total = 0;
  for (const record of state.costModifiers) {
    if (record.kind !== 'amount') continue;
    if ((record.applies ?? 'play') !== applies) continue;
    // A record bound to instances *is* its own scope (CR §6.4.2.2's snapshot, on the payment
    // side): the card it names is the payment's subject or it is not, and whose payment that is
    // follows from the card rather than from the seat the effect was resolved by.
    if (record.cards !== undefined) {
      if (!record.cards.includes(card.id)) continue;
      total += record.amount;
      continue;
    }
    if (record.player !== player) continue;
    if (!matchesCostFilter(state, player, card, record.filter, record.source ?? undefined)) {
      continue;
    }
    total += record.amount;
  }
  for (const record of matchingStaticCostModifiers(state, card, applies, mover, onto)) {
    total += record.amount;
  }
  return total;
}

/**
 * Spend the once-per-turn slot of every static cost modifier a payment actually drew on
 * (CR §6.1.13.2) — called the moment such a payment goes through, never merely when a cost is
 * *priced*: enumerating legal moves prices the same character/location pair many times over
 * without spending anything. A modifier with no `limit` is untouched, so calling this after an
 * ordinary payment is always safe.
 */
export function consumeOnceThisTurnCostModifiers(
  state: GameState,
  card: CardInstance,
  applies: CostApplies = 'play',
  mover?: CardInstance,
  onto?: readonly CardInstance[],
): void {
  for (const record of matchingStaticCostModifiers(state, card, applies, mover, onto)) {
    if (record.useKey !== undefined) state.abilityUses.push(record.useKey);
  }
}

/**
 * Whether a continuous effect forbids this player playing this card at all (CR §6.1.10) —
 * "opponents can't play actions until the start of your next turn". A veto, so it is checked
 * before every payment method is enumerated, not folded into the total.
 */
export function isPlayForbidden(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
): boolean {
  return state.costModifiers.some(
    (record) =>
      record.kind === 'forbid' &&
      record.player === player &&
      matchesCostFilter(state, player, card, record.filter),
  );
}

/**
 * Whether a card carries a payment modifier this player may spend one of — a one-shot record
 * that applies to this payment. Consumed by `payments.ts` once the payment goes through.
 */
export function oneShotModifiersFor(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
  applies: CostApplies = 'play',
): CostModifier[] {
  return state.costModifiers.filter(
    (record) =>
      record.kind !== 'forbid' &&
      record.oneShot === true &&
      (record.kind === 'free' ? applies === 'play' : (record.applies ?? 'play') === applies) &&
      // Bound to instances: the same whole-scope reading `paymentAdjustment` gives it.
      (record.kind === 'amount' && record.cards !== undefined
        ? record.cards.includes(card.id)
        : record.player === player &&
          matchesCostFilter(state, player, card, record.filter, record.source ?? undefined)),
  );
}

/** Spend the one-shot modifiers a completed payment used up (CR 2.2 — free plays too). */
export function consumeOneShotModifiers(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
  applies: CostApplies = 'play',
): void {
  const spent = new Set(oneShotModifiersFor(state, player, card, applies));
  if (spent.size === 0) return;
  state.costModifiers = state.costModifiers.filter((each) => !spent.has(each));
}

// ---------------------------------------------------------------------------
// Total cost
// ---------------------------------------------------------------------------

/**
 * **Underdog** (CR §8.13): "if this is your first turn and you're not the first player, you
 * pay 1 {I} less to play this character". A payment modifier that functions from **hand**
 * (CR §6.1.12). Built from the general controller-relative conditions of the same name
 * (`isYourFirstTurn`, `isStartingPlayer`, `state.ts`) — ticket 107 — so the keyword and any
 * future card that gates a triggered guard or `whileActive` static on the same two questions
 * agree on one answer.
 */
function underdogDiscount(state: GameState, instance: CardInstance): number {
  if (!hasKeyword(state, instance, 'Underdog')) return 0;
  if (isStartingPlayer(state, instance.controller)) return 0;
  return isYourFirstTurn(state, instance.controller) ? -1 : 0;
}

/**
 * The **total ink cost** to play this card (CR §4.3.2) — its printed cost plus every payment
 * modifier that applies, floored at zero.
 *
 * Modifiers apply only while the card is in the zone it would be played from (a hand today);
 * read against a card in play this is the printed cost, which is what the selector limiters
 * ("chosen character with cost 3 or less") need — a cost *reduction* never changes a card's
 * cost characteristic. A "play this from your discard" effect (tickets 16–19) is what widens
 * the zone test.
 */
export function costOf(state: GameState, instance: CardInstance): number {
  const printed = requireDefinition(state.catalog, instance.defId).cost;
  if (instance.zone !== 'hand') return printed;
  const adjustment = paymentAdjustment(state, instance.controller, instance, 'play');
  return Math.max(0, printed + underdogDiscount(state, instance) + adjustment);
}

/** The `{I}` component of an activated ability's cost, after ability payment modifiers. */
export function abilityInkCostOf(state: GameState, card: CardInstance, amount: number): number {
  return Math.max(0, amount + paymentAdjustment(state, card.controller, card, 'ability'));
}

/**
 * The additional ink a character must pay for one quest or challenge attempt.
 *
 * Turn actions have no ordinary ink cost, so the total is the matching payment adjustments
 * alone, floored at zero like every other ink payment. The character is the payment subject:
 * a self static can say "this character can't quest or challenge unless you pay 1 {I}", while
 * a wider static can charge every character its filter covers. Nothing is consumed here —
 * legality enumerates this value freely, and the rules spend any one-shot/once-per-turn
 * modifiers only after the action's payment succeeds.
 */
export function turnActionInkCostOf(
  state: GameState,
  card: CardInstance,
  action: 'quest' | 'challenge',
): number {
  return Math.max(0, paymentAdjustment(state, card.controller, card, action));
}

/**
 * Whether this player may play this card for free right now (CR §6.1.7) — either a floating
 * `free` record a resolved `grant-free-play` effect left behind, or a continuous `free-play`
 * static currently functioning. The static half is re-evaluated here, on every legality
 * check, which is what makes "during your turn, you may play this character for free if …"
 * a live permission rather than a latched flag.
 */
export function canPlayForFree(state: GameState, player: PlayerIndex, card: CardInstance): boolean {
  if (
    state.costModifiers.some(
      (record) =>
        record.kind === 'free' &&
        record.player === player &&
        matchesCostFilter(state, player, card, record.filter, record.source ?? undefined),
    )
  ) {
    return true;
  }
  const permission = zonePlayPermissionFor(state, player, card);
  return permission !== null && permission.cost === 'free';
}

/** A continuous `free-play`-kind static, matched against one card right now (ticket 140). */
export interface ZonePlayPermission {
  /** `'free'` pays nothing; `'full'` pays the card's ordinary printed cost (CR "pay all costs"). */
  readonly cost: 'free' | 'full';
  /** "They enter play exerted" — Cauldron's Power's rider. */
  readonly entersExerted: boolean;
}

/**
 * Whether a continuous `free-play` static grants this player permission to play this card right
 * now, from wherever it is — `null` if none does.
 *
 * The same live reading as `staticCostModifiers`: the printed card carrying the permission must
 * be in the zone its ability functions from (`play` by default, `hand` for the shape that lives
 * on the card being played, CR §6.1.12), its "while …" condition must hold at this very moment,
 * the *target* card must be in the zone the permission reaches (`hand` by default — every card
 * before ticket 140 only ever offered this from hand; `discard` for "you may play characters
 * from your discard"), and it must match the filter — checked with the cost model's own
 * matcher, so the permission can never recurse through `costOf`. The permission belongs to the
 * static's controller: an opponent never inherits "you may play … for free".
 */
export function zonePlayPermissionFor(
  state: GameState,
  player: PlayerIndex,
  card: CardInstance,
): ZonePlayPermission | null {
  for (const source of state.instances) {
    if (source.controller !== player) continue;
    const statics = costStaticsOf(state.catalog, source.defId);
    if (statics.length === 0) continue;
    for (const ability of statics) {
      if (ability.effect.kind !== 'free-play') continue;
      const from: Zone = ability.effect.from ?? 'play';
      if (source.zone !== from) continue;
      if (!staticIsActive(state, source, ability)) continue;
      const targetZone: Zone = ability.effect.zone ?? 'hand';
      if (card.zone !== targetZone) continue;
      if (
        !matchesCostFilter(
          state,
          player,
          card,
          ability.effect.filter,
          source.id,
          costHereOf(state, source),
        )
      ) {
        continue;
      }
      return {
        cost: ability.effect.cost === 'full' ? 'full' : 'free',
        entersExerted: ability.effect.entersExerted === true,
      };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Singing (CR §5.4.4, §8.11, §8.12)
// ---------------------------------------------------------------------------

/** Songs are the Actions with the `Song` classification (CR §5.4.4). */
export function isSong(def: CardDefinition): boolean {
  return def.type === 'Action' && def.subtypes.includes('Song');
}

/**
 * The value a single singer must reach to sing this song (CR §5.4.4): the song's printed
 * cost, plus any "+N cost to sing" payment modifier (CR §8.11.3, added in CR 2.1.0).
 *
 * A singer's own value is `singerValueOf` — its printed cost, or its Singer N if that is
 * higher (CR §8.11).
 */
export function singCostOf(state: GameState, song: CardInstance): number {
  const printed = requireDefinition(state.catalog, song.defId).cost;
  return Math.max(0, printed + paymentAdjustment(state, song.controller, song, 'sing'));
}

/**
 * Sing Together N (CR §8.12): the total value the exerted singers must reach together, after
 * the same "+N cost to sing" modifiers. `null` when the song does not have the keyword.
 */
export function singTogetherRequirementOf(state: GameState, song: CardInstance): number | null {
  if (!hasKeyword(state, song, 'Sing Together')) return null;
  const printed = keywordValue(state, song, 'Sing Together');
  return Math.max(0, printed + paymentAdjustment(state, song.controller, song, 'sing'));
}

/**
 * What one character contributes when it sings — its Singer value, else its printed cost, plus
 * any "characters count as having +N cost to sing songs" continuous modifier functioning on
 * *this* character (Atlantica - Concert Hall's `UNDERWATER ACOUSTICS`, CR §8.11.3's other
 * direction: a bonus to the singer rather than a tax on the song).
 *
 * The same number counts towards **Sing Together**: Singer N says the character "counts as
 * cost N for singing songs" (CR §8.11), and singing together is singing, so a Singer 5
 * character brings 5 to the total rather than its printed cost.
 */
export function singerContributionOf(state: GameState, singer: CardInstance): number {
  const base = singerValueOf(state, singer);
  return Math.max(0, base + paymentAdjustment(state, singer.controller, singer, 'sing'));
}

// ---------------------------------------------------------------------------
// Shift (CR §8.10, and CR 2.2 §8.10.8's six variants)
// ---------------------------------------------------------------------------

/** How a Shift variant chooses what it may be played on top of. */
export type ShiftKind =
  /** Plain `Shift N`: one of your characters with the **same name**. */
  | 'same-name'
  /** `[Classification] Shift N` (Puppy Shift, …): one of your characters of that class. */
  | 'classification'
  /** `Universal Shift N`: **any** of your characters. */
  | 'universal'
  /** `Duo Shift N`: **two** of your characters, one matching each of this card's two names. */
  | 'duo'
  /** `Combo Shift N`: one character matching either name, **or** two, one matching each. */
  | 'combo'
  /** `Potato Shift N`: one of your **items** named Potato — the one Shift onto a non-character. */
  | 'potato';

/** One card's Shift ability, parsed out of the printed variant name. */
export interface ShiftSpec {
  /** The printed alternate cost, before payment modifiers. */
  readonly cost: number;
  /** The printed variant name, e.g. `Puppy Shift`, `Temporary Shift`. */
  readonly variant: string;
  readonly kind: ShiftKind;
  /** The classification a `[Classification] Shift` needs on the base. */
  readonly classification: string | null;
  /**
   * `Temporary [.] Shift N`: at the end of your turn this card returns to hand. Targeting is
   * unchanged; the return is part of ticket 12's stack extraction.
   */
  readonly temporary: boolean;
}

/** The printed Shift variant name on a card, if it has one. */
function shiftVariantName(state: GameState, card: CardInstance): string | null {
  for (const ability of authoredAbilities(state.catalog, card.defId) ?? []) {
    if (ability.kind === 'keyword' && ability.keyword === 'Shift') {
      return ability.variant ?? 'Shift';
    }
  }
  const def = state.catalog.get(card.defId);
  if (def === undefined) return null;
  for (const ability of def.abilities) {
    if (ability.type !== 'keyword' || ability.keyword === undefined) continue;
    if (normaliseKeyword(ability.keyword) === 'Shift') return ability.keyword.trim();
  }
  for (const printed of def.keywords) {
    if (normaliseKeyword(printed) === 'Shift') return printed.trim();
  }
  return null;
}

/**
 * A card's own conditional Shift grant — "if a card left a player's discard this turn, this
 * card gains Shift 0" (Anna - Soothing Sister's UNUSUAL TRANSFORMATION, ticket 109). Read the
 * same way `staticFreePlayFor` reads `free-play` and `alternateCostOf` reads `alternate-cost`:
 * live off the card's own authored IR, in whichever zone the grant's `from` names (`hand` by
 * default, CR §6.1.12) — an ordinary `grant-keyword('Shift', …)` static only ever reaches a
 * card already in play, which is exactly the gap this ticket closes: the grant has to be live
 * *before* the card is played, so it can show up in play-option enumeration from hand.
 */
function staticShiftGrant(state: GameState, card: CardInstance): number | null {
  for (const ability of staticAbilitiesOf(state.catalog, card.defId)) {
    if (ability.effect.kind !== 'grant-shift') continue;
    const from: Zone = ability.effect.from ?? 'hand';
    if (card.zone !== from) continue;
    if (!staticIsActive(state, card, ability)) continue;
    return ability.effect.cost;
  }
  return null;
}

/**
 * Parse a Shift variant name into how it targets. Every variant ends in `Shift`; what comes
 * before it is the whole grammar (rules inventory §5):
 *
 * ```
 * Shift · Universal Shift · Duo Shift · Combo Shift · Potato Shift
 * Puppy Shift (any classification) · Temporary Shift · Temporary Puppy Shift
 * ```
 */
export function parseShiftVariant(variant: string): { kind: ShiftKind; classification: string | null; temporary: boolean } {
  const trimmed = variant.trim();
  const withoutShift = trimmed.replace(/\s*Shift$/u, '').trim();
  const temporary = /^Temporary\b/u.test(withoutShift);
  const qualifier = (temporary ? withoutShift.replace(/^Temporary\b/u, '') : withoutShift).trim();
  switch (qualifier) {
    case '':
      return { kind: 'same-name', classification: null, temporary };
    case 'Universal':
      return { kind: 'universal', classification: null, temporary };
    case 'Duo':
      return { kind: 'duo', classification: null, temporary };
    case 'Combo':
      return { kind: 'combo', classification: null, temporary };
    case 'Potato':
      return { kind: 'potato', classification: null, temporary };
    default:
      return { kind: 'classification', classification: qualifier, temporary };
  }
}

/**
 * This card's Shift ability, or `null` if it has none — printed Shift first, else a live
 * conditional grant (`staticShiftGrant`, ticket 109). A card is never expected to carry both;
 * printed takes priority so a future card layering a conditional grant on top of a printed
 * Shift keyword still reads its own printed variant/cost rather than the grant's.
 */
export function shiftSpecOf(state: GameState, card: CardInstance): ShiftSpec | null {
  const variant = shiftVariantName(state, card);
  if (variant !== null) {
    const parsed = parseShiftVariant(variant);
    return {
      cost: keywordValue(state, card, 'Shift'),
      variant,
      kind: parsed.kind,
      classification: parsed.classification,
      temporary: parsed.temporary,
    };
  }
  const grantedCost = staticShiftGrant(state, card);
  if (grantedCost === null) return null;
  return { cost: grantedCost, variant: 'Shift', kind: 'same-name', classification: null, temporary: false };
}

/**
 * The total cost of playing this card by Shift, after payment modifiers (CR §4.3.2).
 *
 * Both scopes count: an ordinary "you pay 1 {I} less to play your Princess characters" applies
 * whichever method pays the card, and a `shift`-scoped modifier — "for each item card in your
 * discard, you pay 1 {I} less to play this character **using her Shift ability**" — applies
 * only here.
 *
 * `onto` is the candidate base(s) this Shift is being priced against — a `shift`-scoped modifier
 * with an `onto` filter ("you pay 1 {I} less to shift a character on top of **this** character",
 * Belle - Always Reading's DREAMING OF MORE) only matches when its named base is among them, so
 * two legal bases in play at once can price this same card differently. Absent means no base
 * chosen yet, which never matches an onto-keyed modifier — the same "never matches without one"
 * reading `moveCostOf` gives an absent `mover`.
 */
export function shiftTotalCostOf(
  state: GameState,
  card: CardInstance,
  onto?: readonly InstanceId[],
): number | null {
  const spec = shiftSpecOf(state, card);
  if (spec === null) return null;
  const ontoInstances = onto?.map((id) => instanceAt(state, id));
  const adjustment =
    paymentAdjustment(state, card.controller, card, 'play') +
    paymentAdjustment(state, card.controller, card, 'shift', undefined, ontoInstances);
  return Math.max(0, spec.cost + adjustment);
}

/**
 * The alternate cost this card offers, or `null` — "you may banish chosen item of yours to
 * play this character for free" (CR §1.5.5), or its exert-based sibling ("you may exert 4
 * items of yours to play this character for free"), or the discard-to-deck sibling used by
 * Hand-in-the-Box - Sid's Toy.
 *
 * Read straight off the card's authored IR rather than through `modifiers.ts`'s continuous
 * statics, for the same reason `shiftSpecOf` is: the card is in a **hand** when the ability
 * matters, and a static only functions from play (CR §6.1.12 is the exception this makes).
 */
export function alternateCostOf(
  state: GameState,
  card: CardInstance,
): {
  /** What happens to the candidates when the alternate cost is paid. */
  readonly method: 'banish' | 'exert' | 'move-to-bottom-of-deck';
  /** Which of your cards may pay it. */
  readonly selector: Selector;
  readonly count: number;
  readonly window: 'your-turn' | 'any-turn';
  /** Which cost this substitutes — the ordinary play cost, or (ticket 136) the Shift cost. */
  readonly for: 'play' | 'shift';
} | null {
  for (const ability of staticAbilitiesOf(state.catalog, card.defId)) {
    if (ability.effect.kind !== 'alternate-cost') continue;
    return {
      method: ability.effect.payment.kind,
      selector: ability.effect.payment.filter,
      count: ability.effect.count ?? 1,
      window: ability.effect.window ?? 'any-turn',
      for: ability.effect.for ?? 'play',
    };
  }
  return null;
}

/**
 * Names this card counts as having **for Shift** (CR §8.10.3) — "this character also counts as
 * being named King Candy for Shift". A continuous static like any other, so it is read off the
 * cards in play and stops the moment the card leaves.
 */
export function shiftNamesOf(state: GameState, card: CardInstance): readonly string[] {
  if (card.zone !== 'play') return [];
  const found: string[] = [];
  for (const source of state.instances) {
    if (source.zone !== 'play') continue;
    for (const ability of staticAbilitiesOf(state.catalog, source.defId)) {
      if (ability.effect.kind !== 'shift-name') continue;
      if (!staticIsActive(state, source, ability)) continue;
      // The cost model's own matcher, for the cost model's own layering rule: printed
      // characteristics and instance states only, so naming a card can never recurse
      // through `costOf`.
      if (!matchesCostFilter(state, source.controller, card, ability.effect.filter, source.id)) {
        continue;
      }
      if (!found.includes(ability.effect.name)) found.push(ability.effect.name);
    }
  }
  return found;
}

/**
 * "You may play any character with Shift on this character as if this character had **any**
 * name" (Morph - Space Goo's MIMICRY) — the wildcard sibling of `shift-name`: instead of adding
 * one specific name to the base, it satisfies *every* name a Shift card could ask for. Only the
 * name-matching kinds (`same-name`, `duo`, `combo`) ever ask this question — `universal` and
 * `classification` already ignore names, and `potato` is item-only, so a character carrying
 * this static is never a legal `potato` base regardless.
 */
function isWildcardShiftBase(state: GameState, base: CardInstance): boolean {
  for (const ability of staticAbilitiesOf(state.catalog, base.defId)) {
    if (ability.effect.kind !== 'shift-wildcard-base') continue;
    if (!staticIsActive(state, base, ability)) continue;
    if (matchesCostFilter(state, base.controller, base, ability.effect.filter, base.id)) return true;
  }
  return false;
}

/**
 * "You can shift any character on top of this character. (This includes all Shift variants.)"
 * (Morph - Little Imitator's ADVANCED MIMICRY, ticket 142) — the broader sibling of
 * `isWildcardShiftBase`: it waives base eligibility for every Shift kind that reads a base
 * characteristic, including `[Classification] Shift`'s subtype check, which no name-based
 * waiver could satisfy. `universal` needs no waiver (already unconditional) and `potato` is
 * excluded structurally in `isShiftBase` (the `baseDef.type === 'Item'` check runs first), so
 * this function is only ever consulted from the `classification`/`same-name`/`duo`/`combo`
 * branches.
 */
function isAnyVariantShiftBase(state: GameState, base: CardInstance): boolean {
  for (const ability of staticAbilitiesOf(state.catalog, base.defId)) {
    if (ability.effect.kind !== 'shift-any-base') continue;
    if (!staticIsActive(state, base, ability)) continue;
    if (matchesCostFilter(state, base.controller, base, ability.effect.filter, base.id)) return true;
  }
  return false;
}

/** Whether one of your cards in play may be the base of this Shift. */
function isShiftBase(
  state: GameState,
  card: CardInstance,
  spec: ShiftSpec,
  base: CardInstance,
  name?: string,
): boolean {
  if (base.zone !== 'play' || base.controller !== card.controller) return false;
  const baseDef = state.catalog.get(base.defId);
  const def = state.catalog.get(card.defId);
  if (baseDef === undefined || def === undefined) return false;
  if (spec.kind === 'potato') {
    return baseDef.type === 'Item' && baseDef.names.includes('Potato');
  }
  if (baseDef.type !== 'Character') return false;
  // "This character also counts as being named King Candy for Shift" — a name the base has
  // for exactly this question and no other (CR §8.10.3).
  const baseNames = [...baseDef.names, ...shiftNamesOf(state, base)];
  switch (spec.kind) {
    case 'universal':
      return true;
    case 'classification':
      return (
        isAnyVariantShiftBase(state, base) ||
        (spec.classification !== null && baseDef.subtypes.includes(spec.classification))
      );
    case 'same-name':
      return (
        isWildcardShiftBase(state, base) ||
        isAnyVariantShiftBase(state, base) ||
        def.names.some((each) => baseNames.includes(each))
      );
    case 'duo':
    case 'combo':
      if (isWildcardShiftBase(state, base) || isAnyVariantShiftBase(state, base)) return true;
      return name === undefined
        ? def.names.some((each) => baseNames.includes(each))
        : baseNames.includes(name);
  }
}

/**
 * Every legal set of cards this Shift may be played on top of, canonically ordered: single
 * bases in play order first, then the two-card sets `Duo Shift`/`Combo Shift` need, each
 * ascending by instance id.
 *
 * This is Shift's **payment legality**, which is all of Shift this ticket owns. Putting the
 * card on top of the base — the stack, the inherited states, `Temporary Shift`'s extraction —
 * is ticket 12, and until it lands `payments.ts` does not offer these as options.
 */
export function shiftBaseSets(state: GameState, card: CardInstance): InstanceId[][] {
  const spec = shiftSpecOf(state, card);
  if (spec === null) return [];
  const def = state.catalog.get(card.defId);
  if (def === undefined) return [];
  const inPlay = state.instances.filter((each) => isShiftBase(state, card, spec, each));

  const sets: InstanceId[][] = [];
  if (spec.kind !== 'duo') {
    for (const base of inPlay) sets.push([base.id]);
  }
  if (spec.kind === 'duo' || spec.kind === 'combo') {
    // One base per printed name, and never the same card for both (CR 2.2 §8.10.8).
    const [first, second] = def.names;
    if (first !== undefined && second !== undefined && first !== second) {
      const firsts = inPlay.filter((each) => isShiftBase(state, card, spec, each, first));
      const seconds = inPlay.filter((each) => isShiftBase(state, card, spec, each, second));
      for (const a of firsts) {
        for (const b of seconds) {
          if (a.id === b.id) continue;
          const pair = [a.id, b.id].sort((x, y) => x - y);
          if (!sets.some((each) => each.length === 2 && each[0] === pair[0] && each[1] === pair[1])) {
            sets.push(pair);
          }
        }
      }
    }
  }
  return sets;
}
